import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Store} from '@ngrx/store';
import {FormGroup, FormControl} from '@angular/forms';
import {FontAwesomeService} from 'app/services/font-awesome.service';
import {DeliveryType} from 'app/model/delivery-type.interface';
import {AddonService} from '@services/addon.service';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {AppState, OrderCreationState, selectOrderCreation} from '@store/reducers';
import {DeleteAllShipmentsInCart, ShipmentRemove, ShipmentUpdate} from '@store/actions';
import {Shipment} from '@models/shipment.interface';
import {Router} from '@angular/router';
import {trigger, state, style, transition, animate} from '@angular/animations';
import {TranslateService} from '@ngx-translate/core';
import {DHLAdditionalServices} from '@models/additional-services.enum';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-order-shipments',
  templateUrl: './order-shipments.component.html',
  styleUrls: ['./order-shipments.component.scss'],
  animations: [
    trigger('slideToggle', [
      state('void', style({height: '0px', overflow: 'hidden'})),
      state('*', style({height: '*', overflow: 'hidden'})),
      // transition('void <=> *', animate('300ms ease-in-out'))
    ])
  ]
})
export class OrderShipmentsComponent implements OnInit, OnDestroy {
  delivery_types: DeliveryType[];
  private unsubscribe: Subject<void> = new Subject();
  public shipments: Shipment[];
  public additionalServicesForm: FormGroup;
  public currentLanguage: string;

  @Input() shipment: any;
  @Input() index: number;

  @Output() updateShipment: EventEmitter<boolean> = new EventEmitter();
  @Output() additionalServiceEvent: EventEmitter<any> = new EventEmitter();

  public translations: { [serviceCode: string]: { name: string; description?: string; label?: string; placeholder?: string; } } = {};
  public additionalServicesArray: AdditionalService[] = [];
  public chunkedAdditionalServices: any[][] = [];
  public additionalServices: AdditionalServices;

  private numberOfItemsInChunkedColumn = 5;

  constructor(
    public faService: FontAwesomeService,
    private store: Store<AppState>,
    private addonService: AddonService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.store.select('initialInfo')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.delivery_types = data.delivery_types;
      });

    this.currentLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe(() => {
      this.currentLanguage = this.translate.currentLang;
    });

    this.initAdditionalServices();
    // this.additionalServicesForm.get('greenFreight').setValue(true);

    // this.additionalServicesForm.get('greenFreight').valueChanges.subscribe(checked => {
    //   console.log('greenFreight', checked);
    //   this.handleAdditionalServiceChange('greenFreight', {checked});
    // });

    /*
    const checkbox = this.checkboxes.find(cb =>
      cb.nativeElement.getAttribute('formControlName') === 'notification'
    );
    if (checkbox) {
      checkbox.nativeElement.click(); // Triggers (click) handler
    } else {
      console.warn(`Checkbox '${controlName}' not found.`);
    }
    */
  }

  private initAdditionalServices() {
    if (!this.shipment.details.additional_services) {
      return;
    }

    // console.log('test', this.shipment.details.additional_services);

    this.additionalServices = this.shipment.details.additional_services;
    this.additionalServicesArray = Object.values(this.additionalServices);
    this.additionalServicesArray.sort((a, b) => a.sort_order - b.sort_order);
    this.chunkedAdditionalServices = this.chunkArray(this.additionalServicesArray, this.numberOfItemsInChunkedColumn);

    const formGroupConfig = Object.keys(this.additionalServices).reduce((acc, serviceCode) => {
      const service = this.additionalServices[serviceCode];
      const isSelected = this.shipment.selected_additional_services && this.shipment.selected_additional_services[service.code] && this.shipment.selected_additional_services[service.code].selected;
      // console.log('initAdditionalServices', isSelected);
      acc[service.code] = new FormControl(isSelected);

      if (Array.isArray(service.form_fields) && service.form_fields.length > 0) {
        for (const field of service.form_fields) {
          let defaultValue = field.default_value || '';
          switch (serviceCode) {
            case DHLAdditionalServices.PreAdviceByDriver:
              if (field.name === 'phone_number') {
                defaultValue = (this.shipment.delivery && this.shipment.delivery.phone) || this.shipment.to.phone;
              }
              break;
          }
          acc[`${service.code}_${field.name}`] = new FormControl(defaultValue);
        }
      }
      return acc;
    }, {});

    this.additionalServicesForm = new FormGroup(formGroupConfig);
    // this.additionalServicesForm.get('notification').setValue(true);
    this.updateIncompatibleServices();
  }

  // chunkArray(array: any[], chunkSize: number): any[][] {
  //   const chunks = [];
  //   for (let i = 0; i < array.length; i += chunkSize) {
  //     chunks.push(array.slice(i, i + chunkSize));
  //   }
  //   return chunks;
  // }

  chunkArray(array: any[], chunkSize: number = 5, maxColumns: number = 2): any[][] {
    if (array.length <= chunkSize) {
      return [array]; // Single column if 5 or fewer services
    }

    const midIndex = Math.ceil(array.length / maxColumns); // Split into two roughly equal parts

    return [array.slice(0, midIndex), array.slice(midIndex)];
  }

  countShipmentsInCart() {
    let shipments = [];
    this.store.select(selectOrderCreation)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((state: OrderCreationState) => {
        shipments = Object.keys(state.shipments).map(id => {
          return {id, ...state.shipments[id]} as Shipment;
        });
      });

    return shipments.length;
  }

  getDeliveryType(deliveryTypeId) {
    const deliveryType: DeliveryType = this.delivery_types.find((dt) => dt.id === deliveryTypeId);
    return deliveryType ? deliveryType.name : null;
  }

  // get addonInsuranceData() {
  //   return this.addonService.getInsurance(this.shipment.details.addons);
  // }

  handleInsuranceChange(event) {
    this.updateShipment.emit(!!event.value);
  }

  /**
   * Handle changes to the selected additional services
   * @param serviceCode
   * @param event
   */
  handleAdditionalServiceChange(serviceCode, event) {
    const formValues = this.additionalServicesForm.value;
    // console.log('formValues', formValues);
    const formattedFormFields = {};

    Object.keys(formValues).forEach(key => {
      // Split string into service code and field name
      // Example: preAdviceByDriver_phone_number
      const [code, ...fieldParts] = key.split('_');
      const field = fieldParts.join('_');
      if (!formattedFormFields[code]) {
        formattedFormFields[code] = { selected: false, data: {} };
      }
      if (field) {
        if (formattedFormFields[code].selected) {
          formattedFormFields[code].data[field] = formValues[key];
        }
      } else {
        formattedFormFields[code].selected = formValues[key];
      }
    });

    // console.log('event', event);

    // console.log('event', serviceCode,  event, formattedFormFields);

    this.additionalServiceEvent.emit({serviceCode: serviceCode, isActive: event.checked, formFields: formattedFormFields});
  }

  /**
   * Enable and disable incompatible services based on the selected additional services
   */
  updateIncompatibleServices() {
    const selectedServices = Object.keys(this.additionalServices).filter(serviceCode => {
      const control = this.additionalServicesForm.get(serviceCode);
      return control && control.value;
    });

    Object.keys(this.additionalServices).forEach(serviceCode => {
      const service = this.additionalServices[serviceCode];
      const control = this.additionalServicesForm.get(service.code);

      if (control) {
        const isIncompatible = selectedServices.some(selectedServiceCode => {
          const selectedService = this.additionalServices[selectedServiceCode];
          return selectedService.incompatible_services.includes(serviceCode);
        });

        if (isIncompatible || service.disabled) {
          if (!control.disabled) {
            // console.log(`Disabling incompatible service: ${serviceCode}`);
            control.disable();
          }
        } else {
          if (control.disabled) {
            // console.log(`Enabling incompatible service: ${serviceCode}`);
            control.enable();
          }
        }
      }
    });
  }

  /**
   * Get the form control for a specific field in a specific additional service subform
   * @param serviceCode
   * @param fieldName
   */
  getFormFieldControl(serviceCode: string, fieldName: string): FormControl {
    return this.additionalServicesForm.get(`${serviceCode}_${fieldName}`) as FormControl;
  }

  handleAcceptChanges() {
    // console.log('handleAcceptChanges');
    let shipment: Shipment = {...this.shipment as Shipment};
    const details = shipment.details;
    shipment.payment_status.errors.forEach(error => {
      details[error.field] = error.value;
    });
    shipment = {...shipment, details};
    delete shipment.payment_status;
    this.store.dispatch(new ShipmentUpdate({shipment}));
  }

  /**
   * Delete the shipment from the cart
   */
  deleteShipment() {
    this.store.dispatch(new ShipmentRemove({id: this.shipment.id}));

    if (this.countShipmentsInCart() === 0) {
      this.router.navigate(['shipments', 'saved']);
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/order-summary/components/order-shipments/order-shipments.component.ts